/**
 * BE `error_code` → what the UI does. Every 4xx from the 2FA endpoints carries
 * a stable slug next to a localisable `message`; the slug is the contract, the
 * message is not. Shared by the login challenge (FRONT-431) and the settings
 * surface (FRONT-430) so both branch on the same table.
 */

import { isAxiosError } from 'axios';

export type TwoFactorErrorKind =
  /** Show under the field, let the user retry. */
  | 'inline'
  /** The challenge is dead (stale, replayed, disabled meanwhile). Back to credentials. */
  | 'restart'
  /** Rate-limited. Show the wait and disable the form for it. */
  | 'throttled'
  /** The client sent something it never should have. Report, show generic copy. */
  | 'client_bug'
  /** No response at all. */
  | 'network';

export interface TwoFactorErrorInfo {
  kind: TwoFactorErrorKind;
  message: string;
  /** BE slug, or a derived `http_<status>` / `network` / `unknown` for analytics. */
  errorCode: string;
  httpStatus?: number;
  retryAfterSeconds?: number;
}

const INLINE_COPY: Record<string, string> = {
  invalid_2fa_code: 'Invalid code. Check your authenticator and try again.',
  invalid_recovery_code: 'That recovery code is not valid or has already been used.',
  invalid_password: "Password doesn't match.",
  '2fa_already_enabled':
    'Two-factor authentication is already enabled. Disable it first to re-enrol.',
  '2fa_not_configured': "Two-factor setup hasn't been started yet.",
};

const RESTART_CODES = new Set(['challenge_token_stale']);
const CLIENT_BUG_CODES = new Set(['2fa_not_enabled', '2fa_code_required', 'wrong_token_class']);

const GENERIC = 'Something went wrong. Please try again.';
const RESTART_COPY = 'Your sign-in expired. Please sign in again.';
const DEFAULT_RETRY_SECONDS = 60;

function retryAfterFromMessage(message: string | undefined): number {
  const minutes = message?.match(/(\d+)\s*minute/i)?.[1];
  if (minutes) return Number(minutes) * 60;
  const seconds = message?.match(/(\d+)\s*second/i)?.[1];
  return seconds ? Number(seconds) : DEFAULT_RETRY_SECONDS;
}

function throttledCopy(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

export function describeTwoFactorError(error: unknown): TwoFactorErrorInfo {
  if (!isAxiosError(error) || !error.response) {
    return {
      kind: 'network',
      message: 'Connection problem. Check your network and try again.',
      errorCode: 'network',
    };
  }

  const httpStatus = error.response.status;
  const data = error.response.data as
    { error_code?: unknown; message?: unknown; two_factor_pending?: unknown } | undefined;
  const slug = typeof data?.error_code === 'string' ? data.error_code : undefined;
  const beMessage = typeof data?.message === 'string' ? data.message : undefined;
  const errorCode = slug ?? `http_${httpStatus}`;

  if (httpStatus === 429 || slug === '2fa_verify_throttled' || slug === 'login_throttled') {
    const retryAfterSeconds = retryAfterFromMessage(beMessage);
    return {
      kind: 'throttled',
      message: throttledCopy(retryAfterSeconds),
      errorCode,
      httpStatus,
      retryAfterSeconds,
    };
  }

  if (httpStatus === 401 || (slug && RESTART_CODES.has(slug))) {
    return { kind: 'restart', message: RESTART_COPY, errorCode, httpStatus };
  }

  if (data?.two_factor_pending === true || (slug && CLIENT_BUG_CODES.has(slug))) {
    return { kind: 'client_bug', message: GENERIC, errorCode, httpStatus };
  }

  const inline = slug ? INLINE_COPY[slug] : undefined;
  return { kind: 'inline', message: inline ?? beMessage ?? GENERIC, errorCode, httpStatus };
}
